import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { noahToast } from '../lib/noahToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20000;
const HEARTBEAT_MS = 30000;

const AlertsContext = createContext(null);

export const useAlerts = () => useContext(AlertsContext) || { urgentCount: 0, muted: false, toggleMute: () => {} };

const playDing = (ctxRef) => {
  try {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      ctxRef.current = new Ctx();
    }
    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    [988, 1319].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.18, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.4);
    });
  } catch {
    /* audio no disponible */
  }
};

export const AlertsProvider = ({ children }) => {
  const [urgentCount, setUrgentCount] = useState(0);
  const [lastTicket, setLastTicket] = useState(null);
  const [muted, setMuted] = useState(() => localStorage.getItem('deal_alerts_muted') === '1');
  const sinceRef = useRef(new Date().toISOString());
  const audioCtxRef = useRef(null);
  const firstRun = useRef(true);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      localStorage.setItem('deal_alerts_muted', next ? '1' : '0');
      if (!next) {
        playDing(audioCtxRef);
        if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
        toast.success('Avisos de tickets urgentes activados');
      } else {
        toast.info('Avisos silenciados');
      }
      return next;
    });
  }, []);

  // Heartbeat de presencia: marca al agente como "en línea" para las reglas por horario
  useEffect(() => {
    let alive = true;
    const beat = () => { axios.post(`${API}/presence/heartbeat`).catch(() => {}); };
    beat();
    const id = setInterval(() => { if (alive) beat(); }, HEARTBEAT_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Vigilancia de tickets urgentes
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/tickets/alerts?since=${encodeURIComponent(sinceRef.current)}`);
        if (!alive) return;
        setUrgentCount(res.data.open_urgent_count || 0);
        const fresh = res.data.new_tickets || [];
        sinceRef.current = res.data.server_time || sinceRef.current;
        if (firstRun.current) { firstRun.current = false; return; }
        if (fresh.length) {
          const t = fresh[0];
          setLastTicket(t);
          if (!muted) {
            playDing(audioCtxRef);
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`🚨 Ticket ${t.priority}: ${t.subject}`, {
                body: `${t.customer_name || 'Cliente'} necesita atención humana`,
                tag: t.id,
              });
            }
          }
          noahToast('alert', `Ticket ${t.priority}: ${t.subject}`, `${t.customer_name || 'Cliente'} necesita atención humana`, { duration: 8000 });
        }
      } catch {
        /* silencioso */
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [muted]);

  return (
    <AlertsContext.Provider value={{ urgentCount, lastTicket, muted, toggleMute }}>
      {children}
    </AlertsContext.Provider>
  );
};
